import {
  AcademicCapIcon,
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowsRightLeftIcon,
  AtSymbolIcon,
  BellIcon,
  CheckIcon,
  DocumentArrowUpIcon,
  DocumentDuplicateIcon,
  DocumentMinusIcon,
  ForwardIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  LanguageIcon,
  LockOpenIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  TagIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/solid'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { query } from '../../graphql'
import { BUILD_ENV, classNames } from '../../helpers'
import {
  ASSESSMENT_LANGUAGE_CHANGED,
  getAssessmentLanguageChangeLabels
} from '../../helpers/assessment-language-change'
import { parseDocumentMeta } from '../../helpers/talent-details'
import {
  findCustomStatus,
  isCustomStatusId,
  resolveLocalized
} from '../../helpers/talent-statuses'
import Button from '../tailwind/Button'
import Panel from '../tailwind/Panel'
import SectionHeading from '../tailwind/SectionHeading'
import ShowMoreToggle from '../tailwind/ShowMoreToggle'

const HISTORY_UI_ITEMS = {
  // Stelle neu berechnet (aivy-backend-app careerRescore.js). Die Log-Einträge
  // lesen nur Aivy-Admins, Partner bekommen sie gar nicht erst ausgeliefert.
  MATCHING_RESCORED: {
    icon: ArrowPathIcon,
    bgColor: 'bg-gray-400',
    textColor: 'text-white'
  },
  TALENT_INVITED: {
    icon: UserIcon,
    bgColor: 'bg-gray-400',
    textColor: 'text-white'
  },
  ASSESSMENT_LAUNCHED: {
    icon: HandThumbUpIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  ASSESSMENT_STARTED: {
    icon: PlayCircleIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  ASSESSMENT_COMPLETED: {
    icon: CheckIcon,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  },
  EMAIL_BOUNCED: {
    icon: XMarkIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  // Die Einladungs-Mail ging gar nicht erst raus (inviteTalent, MAIL_NOT_SENT).
  // Das Talent steht trotzdem und die Mail kann nachgeschickt werden.
  EMAIL_NOT_SENT: {
    icon: XMarkIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  // Nachgeschickte Einladung, orange wie die anderen Eingriffe des Teams
  INVITATION_RESENT: {
    icon: PaperAirplaneIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  HIRED: {
    icon: CheckIcon,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  },
  TRIAL_PERIOD_SURVIVED: {
    icon: CheckIcon,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  },
  REJECTED: {
    icon: HandThumbDownIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  RESET_EXPIRED: {
    icon: PlayCircleIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  REMINDED: {
    icon: BellIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  INVITED_NEXT_ROUND: {
    icon: AcademicCapIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  APPLICANT_CANCELLED: {
    icon: HandThumbDownIcon,
    bgColor: 'bg-yellow-400',
    textColor: 'text-white'
  },
  SOFT_DELETED: {
    icon: TrashIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  PROFILE_DELETED: {
    icon: TrashIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  // Anonymes Talent mit Einladungslink hat sich in der App abgemeldet. Seit
  // aivy-universal-app 34e3fe7 bleibt der Account dabei bestehen, ältere Einträge
  // stammen noch aus der Zeit, als Abmelden ihn gelöscht hat. In beiden Fällen
  // meldet der Einladungslink danach "bereits verwendet".
  ASSESSMENT_SIGNED_OUT: {
    icon: ArrowRightStartOnRectangleIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  RECOVERED: {
    icon: ArrowPathIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  // Das Team hat den Zugang zurückgesetzt (full-reset.js). Orange wie die
  // anderen Eingriffe des Teams in den Ablauf: Bearbeitungszeit verlängern,
  // Erinnern, Wiederherstellen.
  FULL_RESET: {
    icon: LockOpenIcon,
    bgColor: 'bg-orange-400',
    textColor: 'text-white'
  },
  INTEGRATION_SUCCESS: {
    icon: AtSymbolIcon,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  },
  INTEGRATION_ERROR: {
    icon: XMarkIcon,
    bgColor: 'bg-red-400',
    textColor: 'text-white'
  },
  // Kein Fehler: die Integration hat bewusst nichts übertragen, etwa weil der
  // Space nicht über das ATS kam. Deshalb neutrales Grau statt Rot.
  INTEGRATION_SKIPPED: {
    icon: ForwardIcon,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600'
  },
  APPLICATION_DATA_UPDATED: {
    icon: PencilSquareIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  // Gleiches Icon wie APPLICATION_DATA_UPDATED, aber Grau: die Änderung kam
  // vom Team des Partners, nicht vom Talent selbst.
  APPLICATION_DATA_UPDATED_BY_PARTNER: {
    icon: PencilSquareIcon,
    bgColor: 'bg-gray-400',
    textColor: 'text-white'
  },
  DOCUMENT_UPLOADED: {
    icon: DocumentArrowUpIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  // Blau, wenn das Talent selbst geloescht/ersetzt hat, Grau beim Team (wie
  // APPLICATION_DATA_UPDATED_BY_PARTNER). Die Farbe setzt documentUiItem.
  DOCUMENT_DELETED: {
    icon: DocumentMinusIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  DOCUMENT_REPLACED: {
    icon: ArrowsRightLeftIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  },
  // Das Talent hat die Sprache in der App selbst gewechselt, deshalb Blau wie
  // die anderen Ereignisse, die vom Talent ausgehen.
  [ASSESSMENT_LANGUAGE_CHANGED]: {
    icon: LanguageIcon,
    bgColor: 'bg-blue-500',
    textColor: 'text-white'
  }
}

// Das Talent hat sich abgemeldet oder seinen Account gelöscht, der Space hängt
// aber noch daran. Der Einladungslink ist damit gesperrt, bis der Kunde den
// Zugang zurücksetzt.
const INVITATION_BLOCKING_STATES = ['ASSESSMENT_SIGNED_OUT', 'PROFILE_DELETED']

// Belegt jemand den freien Link neu, schreibt universalAppInit (handleInvitation)
// ASSESSMENT_LAUNCHED. Ein abgemeldetes Talent kommt dabei mit demselben Account
// zurück, owner und fired_by_id stimmen dann wieder überein.
const INVITATION_CLAIMED = 'ASSESSMENT_LAUNCHED'

const ACCESS_RESET = 'FULL_RESET'

const lastIndexWhere = (statuses, predicate) =>
  statuses.reduce(
    (last, status, index) => (predicate(status) ? index : last),
    -1
  )

// Loeschen/Ersetzen schreibt fileControl mit der Rolle des Ausloesers (actor):
// TALENT, PARTNER (Admin/Manager des Kunden) oder ADMIN (Aivy).
const DOCUMENT_STATES = ['DOCUMENT_DELETED', 'DOCUMENT_REPLACED']
const DOCUMENT_ACTOR_SUFFIX = { PARTNER: '_BY_TEAM', ADMIN: '_BY_AIVY' }

const documentName = (fileName, fallback) =>
  (fileName && parseDocumentMeta(fileName).displayName) || fallback || ''

// Eigene Talent-Status des Partners (id custom_*) haben kein eigenes
// Icon-Mapping, sie teilen sich einen Eintrag.
const CUSTOM_STATUS_UI_ITEM = {
  icon: TagIcon,
  bgColor: 'bg-blue-500',
  textColor: 'text-white'
}

// Status, die eine Integration betreffen und daher den Dienstnamen im Text
// auflösen (settings.integrations.<CONTEXT>_title).
const INTEGRATION_STATES = [
  'INTEGRATION_SUCCESS',
  'INTEGRATION_ERROR',
  'INTEGRATION_SKIPPED'
]

// Das Backend liefert als Skip-Grund einen technischen englischen String
// (sharedCode/integrationControl/services.js, skipped(reason)). Bekannte Gründe
// werden übersetzt, unbekannte fallen auf den Rohtext zurück.
const SKIP_REASON_KEYS = {
  'space is not invited via ATS': 'pages.talent.skip_reason_not_via_ats',
  'no callback_url': 'pages.talent.skip_reason_no_callback_url',
  'no career_id': 'pages.talent.skip_reason_no_career_id',
  'no partner_user_id or career_id':
    'pages.talent.skip_reason_no_partner_user_id'
}

const getDynamoDBUrl = (logId, environment) => {
  const tableNames = {
    production: 'Log-h2zruer6rnecpmwc477f524tey-production',
    staging: 'Log-jbuigv6qkrhwtiy5nimzv7wa44-staging',
    develop: 'Log-ou67y5wj6jgsjf5k76keyupg2a-develop'
  }

  const tableName = tableNames[environment] || tableNames.develop

  return `https://eu-central-1.console.aws.amazon.com/dynamodbv2/home?region=eu-central-1#edit-item?itemMode=2&pk=${logId}&route=ROUTE_ITEM_EXPLORER&table=${tableName}`
}

const SpaceHistoryItem = ({
  item,
  index,
  logs,
  onRetryIntegration,
  onFullReset,
  isLoading,
  isAdmin
}) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()

  const handleLogIdClick = () => {
    if (item.logId) {
      window.open(getDynamoDBUrl(item.logId, BUILD_ENV), '_blank')
    }
  }

  const handleCopyLogId = (e) => {
    e.stopPropagation()
    if (item.logId) {
      navigator.clipboard.writeText(item.logId)
      success(t('common.copy_success'))
    }
  }

  const shouldShowLogId =
    isAdmin &&
    item.logId &&
    INTEGRATION_STATES.includes(
      item.content.translationKey?.replace('pages.talent.', '')
    )

  return (
    <div
      className={classNames(
        'relative block',
        index !== logs.length - 1 && 'pb-8'
      )}
    >
      {index !== logs.length - 1 ? (
        <span
          className='absolute left-4 top-4 -ml-0.5 h-full w-px bg-gray-300'
          aria-hidden='true'
        />
      ) : null}
      <div className='relative flex space-x-3'>
        <div>
          <span
            className={classNames(
              item.bgColor,
              'flex size-7 items-center justify-center rounded-full ring-2 ring-white'
            )}
          >
            <item.icon
              className={classNames('size-5', item.textColor)}
              aria-hidden='true'
            />
          </span>
        </div>
        <div className='min-w-0 flex-1 pt-1.5'>
          <div>
            <p className='text-sm text-gray-900'>
              {t(item.content.translationKey, item.content)}
            </p>
            {item.hintKey && (
              <p className='mt-1 text-sm text-gray-500'>{t(item.hintKey)}</p>
            )}
            {item.changes?.length ? (
              <ul className='mt-1 space-y-1'>
                {item.changes.map((change, changeIndex) => (
                  <li key={changeIndex} className='text-sm text-gray-500'>
                    <span className='font-medium text-gray-700'>
                      {/* label_key: sprachneutral gespeicherte Labels (Dashboard-Edits)
                          werden erst hier in der Sprache des Betrachters aufgelöst;
                          label: von der App/Partnern definierte Klartext-Labels. */}
                      {change.label_key ? t(change.label_key) : change.label}:
                    </span>{' '}
                    <span className='text-gray-400 line-through'>
                      {change.before}
                    </span>
                    <span className='mx-1' aria-hidden='true'>
                      →
                    </span>
                    <span className='text-gray-900'>{change.after}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {shouldShowLogId && (
              <div className='mt-2 flex items-center gap-2'>
                <button
                  onClick={handleLogIdClick}
                  className='font-mono inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200'
                  title='Open in DynamoDB'
                >
                  <span className='text-xs'>Log ID: {item.logId}</span>
                </button>
                <button
                  onClick={handleCopyLogId}
                  className='inline-flex items-center rounded-md bg-gray-100 p-1 text-gray-700 hover:bg-gray-200'
                  title='Copy Log ID'
                >
                  <DocumentDuplicateIcon className='size-3' />
                </button>
              </div>
            )}
            {item.content.translationKey === 'pages.talent.INTEGRATION_ERROR' &&
              onRetryIntegration && (
                <Button.SoftXS
                  className='mt-2'
                  minWidth='min-w-0'
                  text={t('pages.talent.INTEGRATION_RETRY')}
                  onClick={onRetryIntegration}
                  isLoading={isLoading}
                />
              )}
            {onFullReset && (
              <Button.SoftXS
                className='mt-2'
                minWidth='min-w-0'
                text={t('space_actions.label_FULL_RESET')}
                onClick={onFullReset}
              />
            )}
          </div>
          <div className='mt-1 whitespace-nowrap text-right text-xs text-gray-500'>
            <p>{item.date}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

SpaceHistoryItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  logs: PropTypes.array.isRequired,
  onRetryIntegration: PropTypes.func,
  onFullReset: PropTypes.func,
  isLoading: PropTypes.bool,
  isAdmin: PropTypes.bool
}

const SpaceHistory = ({ space, spaceActions }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { error, success } = useNotificationContext()
  const { isAdmin } = useUserContext()
  const { partner } = usePartnerContext()

  const [extended, setExtended] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const resetMatchingAndIntegration = () => {
    if (isResetting) return

    setIsResetting(true)

    query({
      query: 'spaceControl',
      variables: { action: 'resetMatchingForSpace', user_id: space.owner }
    })
      .then(() => {
        success(
          t('pages.talent.INTEGRATION_SUCCESS', {
            remote_service: 'Teamtailor'
          })
        )
        // Reload the page to refresh the logs
        window.location.reload()
      })
      .catch((err) => {
        error()
        console.error(err)
      })
      .finally(() => setIsResetting(false))
  }

  // Der Button am Abmelde-Eintrag öffnet denselben Dialog wie "Aktionen →
  // Zugang zurücksetzen", inklusive Sperre bei archivierter Stelle (D16).
  const fullResetAction = spaceActions?.actions?.find(
    ({ key }) => key === 'FULL_RESET'
  )
  const openFullReset =
    fullResetAction &&
    (fullResetAction.available
      ? fullResetAction.onClick
      : fullResetAction.handleActionNotAvailable)

  const sortedLogs = space.logs.sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )

  // Hinweis und Zurücksetzen gehören nur an den letzten sperrenden Eintrag, und
  // nur solange danach niemand den Link neu belegt hat. Sonst tauchen sie wieder
  // auf, sobald ein abgemeldetes Talent nach dem Reset zurückkommt.
  const statuses = sortedLogs.map(({ data }) => JSON.parse(data).status)
  const lastBlockingIndex = lastIndexWhere(statuses, (status) =>
    INVITATION_BLOCKING_STATES.includes(status)
  )
  const lastClaimIndex = lastIndexWhere(
    statuses,
    (status) => status === INVITATION_CLAIMED
  )
  const lastResetIndex = lastIndexWhere(
    statuses,
    (status) => status === ACCESS_RESET
  )

  const logs = sortedLogs
    .map(({ id: logId, data, createdAt, fired_by_id }, index) => {
      const {
        context,
        status,
        option,
        message,
        changes,
        label,
        actor,
        file_name,
        replaced_by,
        replaced_by_file_name,
        old_score,
        new_score,
        old_revision,
        new_revision
      } = JSON.parse(data)
      const isCustomStatus = isCustomStatusId(status)
      const ui_item = isCustomStatus
        ? CUSTOM_STATUS_UI_ITEM
        : HISTORY_UI_ITEMS[status]

      if (!ui_item) return null

      let translationKey = isCustomStatus
        ? 'pages.talent.CUSTOM_STATUS'
        : 'pages.talent.' + status

      if (option === 'VIA_EMAIL' || option === 'VIA_LINK') {
        translationKey += `_${option}`
      }

      const createdAtDate = new Date(createdAt)
      const date = format(createdAtDate, 'd. MMMM yyyy', {
        locale: { de, en }[language]
      })
      const time = format(createdAtDate, 'HH:mm', {
        locale: { de, en }[language]
      })

      const result = {
        id: index,
        logId,
        ...ui_item,
        date: t('pages.talent.date', { date, time }),
        content: { translationKey, reason: message }
      }

      if (INTEGRATION_STATES.includes(status)) {
        result.content.remote_service = t(
          `settings.integrations.${context}_title`
        )
      }

      // Label aus dem aktuellen Katalog, sonst der Snapshot aus dem Log
      // (Status wurde umbenannt oder archiviert).
      if (isCustomStatus) {
        const definition = findCustomStatus(partner.talentStatuses, status)
        result.content.label =
          resolveLocalized(definition?.label || label, language) || status
      }

      if (DOCUMENT_STATES.includes(status)) {
        const suffix = DOCUMENT_ACTOR_SUFFIX[actor] || ''
        result.content.translationKey += suffix
        result.content.reason = documentName(file_name, message)
        if (status === 'DOCUMENT_REPLACED') {
          result.content.replaced_by = documentName(
            replaced_by_file_name,
            replaced_by
          )
        }
        if (suffix) result.bgColor = 'bg-gray-400'
      }

      if (status === 'MATCHING_RESCORED') {
        const score = (value) =>
          typeof value === 'number' ? Math.round(value) : '–'
        Object.assign(result.content, {
          old_score: score(old_score),
          new_score: score(new_score),
          old_revision: old_revision ?? 1,
          new_revision
        })
        return result
      }

      if (status === 'INTEGRATION_SKIPPED' && SKIP_REASON_KEYS[message]) {
        result.content.reason = t(SKIP_REASON_KEYS[message])
      }

      // Sprachwechsel des Talents als ein Satz mit Sprachnamen plus ruhigem
      // Hinweis, ohne die generische Vorher/Nachher-Zeile. Ohne lesbare Codes
      // bleibt der Eintrag verborgen, ein Satz mit Lücken hilft niemandem.
      if (status === ASSESSMENT_LANGUAGE_CHANGED) {
        const languages = getAssessmentLanguageChangeLabels(changes, t)
        if (!languages) return null

        Object.assign(result.content, languages)
        result.hintKey = 'pages.talent.ASSESSMENT_LANGUAGE_CHANGED_hint'

        return result
      }

      // Hinweis und Zurücksetzen nur, solange der Space noch an diesem Account
      // hängt und nicht abgeschlossen ist. Nach einem Reset, nach der Rückkehr
      // des Talents oder bei vorliegenden Ergebnissen bleibt nur der Satz stehen.
      if (INVITATION_BLOCKING_STATES.includes(status)) {
        const blocksInvitation =
          index === lastBlockingIndex &&
          lastClaimIndex < index &&
          !!fired_by_id &&
          fired_by_id === space.owner &&
          space.progress !== 100

        if (blocksInvitation) {
          result.hintKey = `pages.talent.${status}_hint`
          result.canFullReset = true
        }

        return result
      }

      // Der Hinweis sagt, was jetzt gilt, und verschwindet, sobald es nicht mehr
      // gilt: Öffnet jemand den freien Link, steht danach ASSESSMENT_LAUNCHED im
      // Verlauf und der Space hat wieder einen owner.
      if (status === ACCESS_RESET) {
        const invitationFree =
          index === lastResetIndex && lastClaimIndex < index && !space.owner

        if (invitationFree) {
          result.hintKey = 'pages.talent.FULL_RESET_hint'
        }

        return result
      }

      // Feldweise Vorher/Nachher-Details (aktuell: APPLICATION_DATA_UPDATED aus der App).
      if (Array.isArray(changes) && changes.length) {
        result.changes = changes
      }

      return result
    })
    .filter((item) => item)

  if (!logs.length) return null

  const INITIAL_COUNT = 5
  const canCollapse = logs.length > INITIAL_COUNT
  const visibleLogs =
    !canCollapse || extended ? logs : logs.slice(0, INITIAL_COUNT)
  const hiddenCount = logs.length - INITIAL_COUNT

  return (
    <Panel>
      <section aria-labelledby='timeline-title'>
        <SectionHeading
          id='timeline-title'
          title={t('pages.talent.history_title')}
          description={t('pages.talent.history_description')}
        />
        <div className='border-t border-gray-300 pt-5'>
          <div className='flow-root'>
            <ul role='list'>
              {visibleLogs.map((item, index) => (
                <li key={index}>
                  <SpaceHistoryItem
                    item={item}
                    index={index}
                    logs={logs}
                    onRetryIntegration={
                      item.content.translationKey ===
                      'pages.talent.INTEGRATION_ERROR'
                        ? resetMatchingAndIntegration
                        : undefined
                    }
                    onFullReset={item.canFullReset ? openFullReset : undefined}
                    isLoading={isResetting}
                    isAdmin={isAdmin}
                  />
                </li>
              ))}
            </ul>
            {canCollapse && (
              <ShowMoreToggle
                expanded={extended}
                onToggle={() => setExtended(!extended)}
                moreLabel={t('pages.talent.list_show_more', {
                  count: hiddenCount
                })}
                lessLabel={t('pages.talent.show_less')}
              />
            )}
          </div>
        </div>
      </section>
    </Panel>
  )
}

SpaceHistory.propTypes = {
  space: PropTypes.object.isRequired,
  spaceActions: PropTypes.object
}

export default SpaceHistory
